import { useEffect, useState } from "react";
import { Pill, Loader2, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, formatDose } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "");

/** Read-only active medication list for the logged-in inmate. */
export function MyMedicationsModal({ open, onOpenChange }) {
    const [meds, setMeds] = useState(null);

    useEffect(() => {
        if (!open) return;
        setMeds(null);
        api.get("/inmate/medications").then((r) => setMeds(r.data || [])).catch(() => setMeds([]));
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="my-medications-modal" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Pill className="w-5 h-5 text-phoenix-orange" /> My Medications
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">Your current active prescriptions. Talk to your provider with any questions.</p>
                </DialogHeader>

                {meds === null ? (
                    <div className="py-12 text-center text-sm text-muted-foreground">
                        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading your medications…
                    </div>
                ) : meds.length === 0 ? (
                    <div className="py-10 text-center" data-testid="my-medications-none">
                        <Pill className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">You have no active prescriptions on file.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {meds.map((m) => (
                            <div key={m.id} data-testid={`my-med-${m.id}`} className="bg-white border border-border/60 rounded-2xl p-4 shadow-phoenix-card">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="font-display font-semibold text-phoenix-navy">
                                        {m.medication_name}
                                        {m.dosage && <span className="text-muted-foreground font-normal"> · {formatDose(m.dosage)}</span>}
                                    </div>
                                    {m.is_controlled && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-700 shrink-0">
                                            <ShieldAlert className="w-3 h-3" /> Controlled
                                        </span>
                                    )}
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">
                                    {[m.frequency, cap(m.route)].filter(Boolean).join(" · ") || "As directed"}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1.5">
                                    {m.prescribed_by && <>Prescribed by {m.prescribed_by}</>}
                                    {m.signed_at && <> · Started {fmtDate(m.signed_at)}</>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
